import { createSelector } from '@reduxjs/toolkit';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { AppState, useSelector } from '@/redux/helper';
import { createSortedHabitatsSelector } from '@/redux/selectors';

import HomeHighlightVideoPlayer from './HomeHighlightVideoPlayer';
import HomeLiveStreamPlayer from './HomeLiveStreamPlayer';
import HomeVideoPlayerContainer from './HomeVideoPlayerContainer';

export type MiniPlayerContentMode = 'live-stream' | 'highlight';

interface HomeVideoSectionProps {
  highlight: any;
  miniPlayerContentMode: MiniPlayerContentMode;
  onNextHighlight: () => void;
  onSwitchContentMode: () => void;
}

const selectIsAllHabitatsOffline = createSelector(
  (state: AppState) => state.allHabitats,
  (habitats: any[]) => habitats.length > 0 && habitats.every(({ isStreamOn }) => !isStreamOn),
);

const HomeVideoSection = ({
  highlight,
  miniPlayerContentMode,
  onNextHighlight,
  onSwitchContentMode,
}: HomeVideoSectionProps) => {
  const [isMiniPlayerClosed, setIsMiniPlayerClosed] = useState(false);
  const preferLiveStreamMutedRef = useRef(true);
  const preferredHabitatIdRef = useRef<string>();
  const selectHabitatForStream = useMemo(
    () =>
      createSelector(
        createSortedHabitatsSelector(false),
        (_: AppState, habitatId: string | undefined) => habitatId,
        (sortedHabitats, habitatId) => {
          const habitat = habitatId ? sortedHabitats.find((habitat) => habitat._id === habitatId) : undefined;
          if (habitat && habitat.isStreamOn) {
            return habitat;
          }
          return sortedHabitats.find(({ isStreamOn }) => isStreamOn);
        },
      ),
    [],
  );

  const isAllHabitatsOffline = useSelector(selectIsAllHabitatsOffline);
  const habitatForStream = useSelector((state) => selectHabitatForStream(state, preferredHabitatIdRef.current));

  useEffect(() => {
    preferredHabitatIdRef.current = habitatForStream?._id;
  }, [habitatForStream?._id]);

  useEffect(() => {
    if (miniPlayerContentMode) {
      setIsMiniPlayerClosed(false);
    }
  }, [miniPlayerContentMode]);

  const isHighlightMinimized = miniPlayerContentMode === 'highlight' && !isAllHabitatsOffline;
  const isLiveStreamMinimized = miniPlayerContentMode === 'live-stream';

  return (
    <div className="group/home-video-section preflight relative bg-slate-900 after:block after:pt-[min(56.25%,100vh-var(--headerHeight))]">
      {(!isHighlightMinimized || !isMiniPlayerClosed) && (
        <HomeVideoPlayerContainer miniMode={isHighlightMinimized} onExpand={onSwitchContentMode}>
          {highlight && (
            <HomeHighlightVideoPlayer
              videoURL={highlight.rawVideoURL}
              videoId={highlight._id}
              videoTitle={highlight.title}
              onNextHighlight={onNextHighlight}
              miniMode={isHighlightMinimized}
              onClose={isHighlightMinimized ? () => setIsMiniPlayerClosed(true) : undefined}
            />
          )}
        </HomeVideoPlayerContainer>
      )}
      {!isAllHabitatsOffline && (
        <HomeVideoPlayerContainer miniMode={isLiveStreamMinimized} onExpand={onSwitchContentMode}>
          {habitatForStream && (
            <HomeLiveStreamPlayer
              key={habitatForStream._id}
              preferLiveStreamMutedRef={preferLiveStreamMutedRef}
              miniMode={isLiveStreamMinimized}
              habitatSlug={habitatForStream.slug}
              zooSlug={habitatForStream.zoo.slug}
            />
          )}
        </HomeVideoPlayerContainer>
      )}
    </div>
  );
};

export default HomeVideoSection;
